import type { CartItem } from './types'
import { updateCartItem } from './_service'

// Дизайн по умолчанию — совпадает с DEFAULT_DESIGN в _list.ts.
const DEFAULT_DESIGN = 'gift'

interface CertificateDesign {
  code: string
  name: string
}

// Блок «Выбор дизайна»: слева переключатели между сертификатами (если их
// несколько) и миниатюры дизайнов, справа — предпросмотр готового сертификата.
// Выбранный дизайн пишется в parameters.design позиции корзины — сервер
// сохранит его в certificates.design.
export class CartDesigns {
  private container: HTMLElement
  private switchEl: HTMLElement
  private optionsEl: HTMLElement
  private captionEl: HTMLElement
  private previewImgEl: HTMLImageElement
  private designs: CertificateDesign[] = []
  private items: CartItem[] = []
  private allItems: CartItem[] = []
  private activeIndex: number = 0
  private onChange?: (_items: CartItem[]) => void

  constructor(container: HTMLElement, onChange?: (_items: CartItem[]) => void) {
    this.container = container
    this.onChange = onChange

    this.switchEl = container.querySelector<HTMLElement>(
      '[data-cart-designs-switch]'
    )!
    this.optionsEl = container.querySelector<HTMLElement>(
      '[data-cart-designs-options]'
    )!
    this.captionEl = container.querySelector<HTMLElement>(
      '[data-cart-designs-caption]'
    )!
    this.previewImgEl = container.querySelector<HTMLImageElement>(
      '[data-cart-designs-preview]'
    )!

    try {
      this.designs = JSON.parse(container.dataset.designs || '[]')
    } catch {
      this.designs = []
    }

    this.container.addEventListener('click', this.onClick)
  }

  public update(items: CartItem[]) {
    this.allItems = items
    this.items = items.filter((item) => item.type === 'digital')
    this.container.classList.toggle('is-hidden', this.items.length === 0)
    if (this.items.length === 0) return

    if (this.activeIndex >= this.items.length) this.activeIndex = 0
    this.render()
  }

  private get activeItem(): CartItem | undefined {
    return this.items[this.activeIndex]
  }

  private get activeDesign(): string {
    return this.activeItem?.parameters?.design || DEFAULT_DESIGN
  }

  private render() {
    this.renderSwitch()
    this.renderOptions()
    this.renderPreview()
  }

  // Переключатели «Дизайн сертификата на 10 000 ₽» — только если в корзине
  // несколько электронных сертификатов.
  private renderSwitch() {
    this.switchEl.classList.toggle('is-hidden', this.items.length < 2)
    if (this.items.length < 2) {
      this.switchEl.innerHTML = ''
      return
    }

    this.switchEl.innerHTML = this.items
      .map((item, index) => {
        const active = index === this.activeIndex
        const sign = item.currency === 'BYN' ? 'BYN' : '₽'
        return `<button type="button" role="tab" aria-selected="${active}" class="cart-designs__tab${
          active ? ' is-active' : ''
        }" data-cart-designs-tab="${index}">
            Дизайн сертификата на ${item.price.toLocaleString('ru-RU')} ${sign}
          </button>`
      })
      .join('')
  }

  private renderOptions() {
    const selected = this.activeDesign

    this.optionsEl.innerHTML = this.designs
      .map((design) => {
        const active = design.code === selected
        return `<button type="button" role="radio" aria-checked="${active}" class="cart-designs__option${
          active ? ' is-active' : ''
        }" data-cart-designs-option="${design.code}" title="${design.name}">
            <img loading="lazy" src="/images/certificates/designs/illustrations/${design.code}@1x.webp" alt="${design.name}">
          </button>`
      })
      .join('')

    const selectedName =
      this.designs.find((design) => design.code === selected)?.name || selected
    this.captionEl.textContent = `Дизайн: ${selectedName}`
  }

  private renderPreview() {
    const item = this.activeItem
    const selected = this.activeDesign
    const design = this.designs.find((entry) => entry.code === selected)
    const name = design?.name || selected

    // Генерируется сервером: тот же рендерер, что будет собирать PDF
    // для письма, — с реальным номиналом и замаскированными кодом/сроком.
    const amount = item ? `&amount=${item.price}` : ''
    const currency = item?.currency === 'BYN' ? '&currency=BYN' : ''
    this.previewImgEl.src = `/certificates/preview?design=${selected}${amount}${currency}`
    this.previewImgEl.alt = `Готовый сертификат, дизайн «${name}»`
  }

  private onClick = (event: MouseEvent) => {
    const target = event.target as HTMLElement

    const tab = target.closest<HTMLElement>('[data-cart-designs-tab]')
    if (tab) {
      this.activeIndex = parseInt(tab.dataset.cartDesignsTab || '0', 10)
      this.render()
      return
    }

    const option = target.closest<HTMLElement>('[data-cart-designs-option]')
    if (option) {
      this.selectDesign(option.dataset.cartDesignsOption as string)
    }
  }

  private selectDesign(design: string) {
    const item = this.activeItem
    if (!item || item.parameters?.design === design) return

    item.parameters = { ...item.parameters, design }
    updateCartItem(item)
    this.render()
    this.onChange?.(this.allItems)
  }
}
